import React, { FC } from 'react';
import { motion } from 'framer-motion';
import APP2 from '../../../../app/assets/images/projectImage/aichatImage/app2.png'
import APPLayout1 from '../../../../app/assets/images/projectImage/aichatImage/applayout1.png'
import APPLayout2 from '../../../../app/assets/images/projectImage/aichatImage/applayout2.png'
import APPLayout3 from '../../../../app/assets/images/projectImage/aichatImage/applayout3.png'

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2
        }
    }
};

const hoverElevate = {
    y: -5,
    //   boxShadow: "0 10px 20px rgba(0, 0, 0, 0.2)",
    transition: { duration: 0.3 }
};

// Exaggerated animations
const crazyRotate = {
    rotate: [0, 15, -15, 10, -10, 5, -5, 0],
    transition: {
        duration: 1.5,
        repeat: Infinity,
        repeatType: "reverse" as const
    }
};

const pulseGlow = {
    filter: ["brightness(1)", "brightness(1.5)", "brightness(1)"],
    scale: [1, 1.1, 1],
    transition: {
        duration: 1.2,
        repeat: Infinity,
        repeatType: "mirror" as const
    }
};

const jiggleJump = {
    y: [0, -20, 10, -5, 0],
    x: [0, 10, -10, 5, 0],
    rotate: [0, 5, -5, 2, 0],
    transition: {
        duration: 1,
        repeat: Infinity,
        repeatType: "reverse" as const
    }
};

interface AIChatProjectOverviewProps {
    aiChatProject: any;
}

const AIChatProjectOverview: FC<AIChatProjectOverviewProps> = ({ aiChatProject }) => {
    const { projectOverviewContent } = aiChatProject?.aichatProjectDetails;
    return (
        <div>
            {/* Overview Section */}
            {projectOverviewContent.map((item: any) => {
                return (
                    <motion.div
                        className={`flex flex-col ${item.key === 2 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center justify-center xl:px-16 gap-6 md:gap-2 py-8 md:py-0`}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={staggerContainer}
                    >
                        <motion.img
                            variants={item.key === 2 ? fadeInRight : fadeInLeft}
                            src={item.image}
                            className='w-full md:w-[45%] rounded-lg'
                            whileHover={hoverElevate}
                        />
                        <motion.div
                            variants={item.key !== 2 ? fadeInRight : fadeInLeft}
                            className={`w-full ${item.key !== 2 ? 'md:w-[45%]' : 'md:w-[40%]'} px-2 md:px-0`}
                            whileHover={{ x: item.key !== 2 ? 5 : -5, transition: { duration: 0.3 } }}
                        >
                            <div className='text-2xl md:text-3xl font-bold text-center md:text-start'>{item.title}</div>
                            <div className={`text-[#e0e0e0] pt-2 text-base md:text-lg text-center md:text-start ${item.key !== 2 ? 'w-full md:w-[90%]' : 'w-full'}`}>{item.description}</div>
                        </motion.div>
                    </motion.div>
                )
            })}

            {/* App Layout Images Section */}
            <motion.div
    className='grid grid-cols-1 sm:grid-cols-3 items-center justify-items-center gap-4 sm:gap-3 md:gap-6 lg:gap-8 py-10 px-4'
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.3 }}
    variants={staggerContainer}
>
    <motion.img
        variants={fadeInUp}
        src={APPLayout1}
        className="w-[50%] md:w-[65%] xl:w-[60%]  rounded-lg object-contain"
        whileHover={crazyRotate}
    />
    <motion.img
        variants={fadeInUp}
        src={APPLayout2}
        className="w-[50%] md:w-[85%] xl:w-[75%] rounded-lg object-contain"
        whileHover={pulseGlow}
        animate={{
            scale: [1, 1.05, 0.95, 1],
            transition: {
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse" as const
            }
        }}
        drag
        dragConstraints={{ left: -50, right: 50, top: -50, bottom: 50 }}
        whileDrag={{ scale: 1.2 }}
    />
    <motion.img
        variants={fadeInUp}
        src={APPLayout3}
        className="w-[50%] md:w-[65%] xl:w-[60%] rounded-lg object-contain"
        whileHover={crazyRotate}
    />
</motion.div>

            {/* Objectives Section */}
            <motion.div
                className='flex flex-col md:flex-row-reverse items-center justify-center lg:px-16 gap-6 md:gap-2 py-8 md:py-0'
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={staggerContainer}
            >
                <motion.img
                    variants={fadeInRight}
                    src={APP2}
                    className='w-full md:w-[45%] rounded-lg'
                    whileHover={hoverElevate}
                />
                <motion.div
                    variants={fadeInLeft}
                    className='w-full md:w-[40%] px-2 md:px-0'
                    whileHover={{ x: -5, transition: { duration: 0.3 } }}
                >
                    <div className='text-2xl md:text-3xl font-bold text-center md:text-start'>Features</div>
                    <div className='text-[#e0e0e0] pt-2 text-base md:text-lg w-full md:w-[80%] text-center md:text-start'>The app supports over 140 languages, making it accessible to a global audience. Students can also customize their chat experience by selecting different tones—like casual, formal, or friendly—and even choose character-based tutors for a more engaging interaction. Built with privacy in mind, all conversations are securely stored, and no personal data is shared. Powered by advanced AI models like GPT-3 and GPT-4, the platform ensures high-quality responses and a seamless, intuitive user experience across devices.</div>
                </motion.div>
            </motion.div>
        </div>
    )
}

export default AIChatProjectOverview
